/**
 * UI entry point: Octopus Energy Texas Savings Estimate (TODO-005, TODO-007).
 *
 * Single store (`state` + rep `settings`) → `buildBid` on change (debounced)
 * → render ticker, upgrade cards, bid, drawer and print sheet.
 *
 * V1 = Octopus plan + FREE Octopus battery, NO SOLAR.
 */
import { MONTHS_LONG, MONTHS_SHORT, termView, usd } from "./viewmodel";
import { BATTERY_SIZES, clearSession, defaultRepSettings, defaultState, loadRep, loadState, regionsForTdu, save, setTdu, toBidInput, type AppState, type RepSettings, type Step } from "./state";
import { buildBid, type BidResult } from "../engine/bid";
import { TDUS, type TduId } from "../config";
import { renderBid } from "./render/bid";
import { drawerFormHtml, drawerLiveHtml } from "./render/assumptions";
import { printSheetHtml } from "./render/print";

export const APP_NAME = "Octopus Texas Bid Tool";

type Obj = Record<string, unknown>;

function getPath(o: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, k) => (acc == null ? undefined : (acc as Obj)[k]), o);
}
function setPath(o: unknown, path: string, value: unknown): void {
  const keys = path.split(".");
  const last = keys.pop()!;
  const parent = keys.reduce<unknown>((acc, k) => (acc as Obj)[k], o) as Obj;
  parent[last] = value;
}

function mount(): void {
  const app = document.getElementById("app");
  if (!app) return;
  const $ = <T extends HTMLElement = HTMLElement>(sel: string): T => document.querySelector(sel) as T;
  const store = (() => {
    try {
      return window.localStorage;
    } catch {
      return undefined;
    }
  })();

  let state: AppState = loadState(store);
  let rep: RepSettings = loadRep(store);
  let bid: BidResult | null = null;
  let missing: string[] = [];
  let error: string | null = null;

  const drawer = $<HTMLDialogElement>("#drawer");
  const drawerBody = $("#drawer-body");
  drawerBody.innerHTML = `<div id="drawer-form"></div><div id="drawer-live"></div>`;

  // ── Static dynamic bits ─────────────────────────────────────────────────
  $("#month-grid").innerHTML = MONTHS_SHORT.map(
    (m, i) => `<label class="month-cell"><span>${m}</span><input type="text" inputmode="decimal" autocomplete="off" aria-label="${MONTHS_LONG[i]} kWh" placeholder="kWh" data-bind="monthlyKwh.${i}" /></label>`,
  ).join("");
  $("#battery-sizes").innerHTML = BATTERY_SIZES.map(
    (k) => `<label><input type="radio" name="battSize" value="${k}" data-bind="battery.capacityKwh" /><span>${k} kWh</span></label>`,
  ).join("");
  let chartOpen = false;

  function renderRegions(): void {
    const sel = $<HTMLSelectElement>("#in-region");
    const regions = regionsForTdu(state.tdu);
    sel.innerHTML = regions.map((r) => `<option value="${r.id}">${r.name}</option>`).join("");
    sel.value = state.region;
    const r = regions.find((x) => x.id === state.region);
    $("#region-name").textContent = r ? r.name : "";
  }
  function setRegionOpen(open: boolean): void {
    $("#region-wrap").hidden = !open;
    $("#btn-region").setAttribute("aria-expanded", String(open));
    $("#btn-region").textContent = open ? "Done" : "Change";
  }
  $("#btn-region").addEventListener("click", () => {
    const open = $("#region-wrap").hidden;
    setRegionOpen(open);
    if (open) $<HTMLSelectElement>("#in-region").focus();
  });

  /** State → DOM for every bound control. Only on load / reset / structural change. */
  function syncDom(): void {
    renderRegions();
    for (const el of app!.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-bind]")) {
      const v = getPath(state, el.dataset.bind!);
      if (el instanceof HTMLInputElement && el.type === "radio") el.checked = String(v) === el.value;
      else if (el instanceof HTMLInputElement && el.type === "checkbox") el.checked = Boolean(v);
      else el.value = v == null ? "" : String(v);
    }
    applyVisibility();
  }

  function applyVisibility(): void {
    for (const el of app!.querySelectorAll<HTMLElement>("[data-show]")) {
      const [path, want] = el.dataset.show!.split("=");
      const v = getPath(state, path!);
      el.hidden = want === undefined ? !v : String(v) !== want;
    }
  }

  function showStep(step: Step): void {
    state.step = step;
    app!.dataset.step = String(step);
    for (const p of app!.querySelectorAll<HTMLElement>("[data-panel]")) p.hidden = p.dataset.panel !== String(step);
    for (const b of app!.querySelectorAll<HTMLButtonElement>("[data-goto]")) {
      if (b.dataset.goto === String(step)) b.setAttribute("aria-current", "step");
      else b.removeAttribute("aria-current");
    }
    $<HTMLButtonElement>("#btn-back").hidden = step === 1;
    $("#btn-next").textContent = step === 1 ? "Continue" : step === 2 ? "See your estimate" : "Print leave-behind";
    $("#ticker").hidden = step === 3; // no savings line on the estimate page itself
    window.scrollTo({ top: 0 });
    render();
    persist();
  }

  // ── Compute + render ────────────────────────────────────────────────────
  function compute(): void {
    const r = toBidInput(state, rep);
    error = null;
    if (!r.ok) {
      bid = null;
      missing = r.missing;
      return;
    }
    missing = [];
    try {
      bid = buildBid(r.input);
    } catch (e) {
      bid = null;
      error = e instanceof Error ? e.message : String(e);
    }
  }

  /** One quiet line next to Continue on steps 1–2. */
  function renderTicker(): void {
    const t = $("#ticker");
    if (!bid) {
      t.innerHTML = error ? `<span class="tk-muted">${error}</span>` : `<span class="tk-muted">Add ${missing.join(" and ")} to see savings</span>`;
      return;
    }
    const v = termView(bid, state.term);
    t.innerHTML =
      v.savingsTotal > 0
        ? `<span class="tk-muted">Estimated savings</span> <strong class="tk-num">${usd(v.savingsAvg)}/mo</strong>`
        : `<span class="tk-muted">Octopus estimate ${usd(v.octopusAvg)}/mo</span>`;
  }

  /** Small mint "+$12.00/mo" on Step 2 rows; hidden when off or $0. */
  function renderImpacts(): void {
    const v = bid ? termView(bid, state.term) : null;
    for (const el of app!.querySelectorAll<HTMLElement>("[data-impact]")) {
      const id = el.dataset.impact as "ev" | "nest";
      const row = v?.waterfall.find((r) => r.id === id);
      const show = state[id].enabled && !!row && Math.abs(row.total) >= 0.005;
      el.hidden = !show;
      if (show && row) {
        el.textContent = `${row.avg >= 0 ? "+" : "−"}${usd(Math.abs(row.avg))}/mo`;
        el.classList.toggle("neg", row.avg < 0);
      }
    }
  }

  function renderBidPanel(): void {
    const root = $("#bid-root");
    // Re-rendering replaces the controls; restore keyboard focus afterwards.
    const active = document.activeElement as HTMLElement | null;
    const focusSel = active && root.contains(active)
      ? active.id ? `#${active.id}` : active.dataset.bind ? `[data-bind="${active.dataset.bind}"][value="${(active as HTMLInputElement).value}"]` : null
      : null;
    if (bid) {
      renderBid(root, bid, state, { chartOpen });
    } else {
      root.innerHTML = `<div class="empty">
        <p class="hero-label">Almost there</p>
        <p>${error ? error : `Still need ${missing.join(" and ")}.`}</p>
        <button type="button" class="btn-quiet" data-goto="1">Back to your rate</button></div>`;
    }
    if (focusSel) root.querySelector<HTMLElement>(focusSel)?.focus();
  }

  function renderDrawerLive(): void {
    $("#drawer-live").innerHTML = drawerLiveHtml(bid, missing);
  }
  function renderDrawerForm(): void {
    $("#drawer-form").innerHTML = drawerFormHtml(state, rep);
  }

  function render(): void {
    renderTicker();
    renderImpacts();
    if (state.step === 3) renderBidPanel();
    if (drawer.open) renderDrawerLive();
    $("#print-sheet").innerHTML = bid ? printSheetHtml(bid, state) : "";
  }

  let persistTimer = 0;
  function persist(): void {
    clearTimeout(persistTimer);
    persistTimer = window.setTimeout(() => save(store, state, rep), 300);
  }

  let computeTimer = 0;
  function update(immediate = false): void {
    clearTimeout(computeTimer);
    const run = (): void => {
      compute();
      render();
      persist();
    };
    if (immediate) run();
    else computeTimer = window.setTimeout(run, 150);
  }

  // ── Events ──────────────────────────────────────────────────────────────
  function onBind(e: Event): void {
    const el = e.target as HTMLInputElement | HTMLSelectElement;
    const path = el.dataset?.bind;
    if (!path) return;
    if (el instanceof HTMLInputElement && el.type === "radio" && !el.checked) return;
    const prev = getPath(state, path);
    let v: unknown = el instanceof HTMLInputElement && el.type === "checkbox" ? el.checked : el.value;
    if (typeof prev === "number") v = Number(v);
    if (path === "tdu") {
      setTdu(state, v as TduId);
      renderRegions();
      if (drawer.open) renderDrawerForm();
    } else {
      setPath(state, path, v);
    }
    if (path === "region") {
      renderRegions();
      if (e.type === "change") setRegionOpen(false);
    }
    applyVisibility();
    update(e.type === "change");
  }
  app.addEventListener("input", onBind);
  app.addEventListener("change", onBind);
  // Remember the "Monthly detail" disclosure across re-renders (toggle doesn't bubble).
  $("#bid-root").addEventListener(
    "toggle",
    (e) => {
      if ((e.target as HTMLElement).id === "chart-detail") chartOpen = (e.target as HTMLDetailsElement).open;
    },
    true,
  );

  app.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    const go = t.closest<HTMLElement>("[data-goto]");
    if (go) {
      showStep(Number(go.dataset.goto) as Step);
    }
  });

  $("#btn-back").addEventListener("click", () => showStep(Math.max(1, state.step - 1) as Step));
  $("#btn-next").addEventListener("click", () => {
    if (state.step < 3) showStep((state.step + 1) as Step);
    else doPrint();
  });

  function doPrint(): void {
    compute();
    render();
    if (!bid) {
      showStep(3);
      return;
    }
    window.print();
  }
  $("#btn-print").addEventListener("click", doPrint);

  $("#btn-new").addEventListener("click", () => {
    if (!window.confirm("Start a new customer? This clears the current customer's details.")) return;
    clearSession(store);
    state = defaultState();
    syncDom();
    showStep(1);
    update(true);
  });

  // Drawer
  function openDrawer(): void {
    renderDrawerForm();
    renderDrawerLive();
    if (!drawer.open) drawer.showModal();
  }
  $("#btn-gear").addEventListener("click", openDrawer);
  // Light-dismiss fallback for browsers without <dialog closedby> (Safari).
  function lightDismissFallback(d: HTMLDialogElement): void {
    if ("closedBy" in HTMLDialogElement.prototype) return;
    d.addEventListener("click", (e) => {
      if (e.target !== d) return;
      const r = d.getBoundingClientRect();
      const inside = r.top <= e.clientY && e.clientY <= r.bottom && r.left <= e.clientX && e.clientX <= r.right;
      if (!inside) d.close();
    });
  }
  lightDismissFallback(drawer);

  // Optional "Meet our mascot" easter egg (TODO-007). Never printed.
  const mascot = $<HTMLDialogElement>("#mascot");
  const mascotBtn = $<HTMLButtonElement>("#btn-mascot");
  mascotBtn.addEventListener("click", () => {
    if (!mascot.open) mascot.showModal();
  });
  mascot.addEventListener("close", () => mascotBtn.focus());
  lightDismissFallback(mascot);
  function onRep(e: Event): void {
    const el = e.target as HTMLInputElement | HTMLSelectElement;
    const path = el.dataset?.rep;
    if (!path) return;
    if (el instanceof HTMLInputElement && el.type === "radio" && !el.checked) return;
    setPath(rep, path, el.value);
    if (path.endsWith(".volChoice")) renderDrawerForm();
    update(e.type === "change");
  }
  drawer.addEventListener("input", onRep);
  drawer.addEventListener("change", onRep);
  drawer.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).id === "btn-reset-rep") {
      rep = defaultRepSettings();
      renderDrawerForm();
      update(true);
    }
  });

  document.addEventListener("keydown", (e) => {
    if (!(e.shiftKey && (e.key === "A" || e.key === "a")) || e.ctrlKey || e.metaKey || e.altKey) return;
    if (mascot.open) return; // don't stack the rep drawer over the mascot dialog
    const tgt = e.target as HTMLElement;
    if (tgt.closest("input, textarea, select, [contenteditable]")) return;
    e.preventDefault();
    if (drawer.open) drawer.close();
    else openDrawer();
  });

  window.addEventListener("beforeprint", () => {
    compute();
    $("#print-sheet").innerHTML = bid ? printSheetHtml(bid, state) : "";
  });

  // ── Boot ────────────────────────────────────────────────────────────────
  if (!(state.tdu in TDUS)) state = defaultState();
  syncDom();
  compute();
  showStep(state.step);
  app.setAttribute("data-ready", "true");
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", mount);
  else mount();
}
